import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Chip, Typography } from "@mui/material";
import ExternalServiceIcon from "../../../../components/service/externalServiceIcon";
import { Construction, PendingActions } from "@mui/icons-material";
import { useCRUD } from "../../../../api/crud";
import { EXTERNAL_SERVICES_ENDPOINT } from "../../../../consts";
import { useHasPermission } from "../../../../contexts/auth";
import { ExternalService } from "../../../../types/externalServices";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../../components/ui/detailView";
import { StatusChip } from "../../../../components/ui/statusChip/statusChip.component";
import { StatCard } from "../../../dashboard/dashboard.component";
import {
  ExternalServiceAreasMap,
  getAreaColor,
} from "../../../../components/service/externalServiceAreas/externalServiceAreasMap.component";
import { formatAreaPlace } from "../../../../components/service/externalServiceAreas/externalServiceAreasField.component";

type ExternalServiceBasicInfoProps = {
  previewId?: string | number;
};

export const ExternalServiceBasicInfo = ({ previewId }: ExternalServiceBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { get } = useCRUD(EXTERNAL_SERVICES_ENDPOINT);
  const [externalService, setExternalService] = useState<ExternalService | null>(null);
  const canView = useHasPermission("service.view_externalservice");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchExternalService = async () => {
      if (entityId) {
        const response = await get(entityId);
        setExternalService(response?.data ?? null);
      }
    };

    window.addEventListener("externalServiceUpdated", fetchExternalService);
    fetchExternalService();
    return () => {
      window.removeEventListener("externalServiceUpdated", fetchExternalService);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canView) {
    return null;
  }

  if (!externalService) return <DetailPage loading />;

  const { areas } = externalService;
  // The jobs cards lead to the jobs tab - not from inside a preview modal.
  const openJobsTab = previewId ? undefined : () => navigate(`/service/external-services/${externalService.id}/jobs`);

  return (
    <DetailPage>
      <DetailHeader
        icon={<ExternalServiceIcon />}
        title={externalService.name}
        subtitle={areas.length > 0 ? areas.map((area) => area.city).join(", ") : undefined}
        chips={!externalService.is_active && <StatusChip color="default" label={t("Inactive")} />}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <StatCard
          title={t("Open jobs")}
          value={externalService.open_jobs_count}
          icon={<PendingActions />}
          color="#ed6c02"
          onClick={openJobsTab}
        />
        <StatCard
          title={t("All jobs")}
          value={externalService.jobs_count}
          icon={<Construction />}
          color="#1976d2"
          onClick={openJobsTab}
        />
      </Box>

      <DetailGrid>
        <DetailSection title={t("Contact Information")}>
          <DetailRow label={t("Name")} value={externalService.name} />
          <DetailRow label={t("Phone Number")} value={externalService.phone_number} />
          <DetailRow label={t("Email")} value={externalService.email} />
          <DetailRow label={t("Notes")} value={externalService.notes} multiline />
        </DetailSection>
      </DetailGrid>

      <DetailSection
        title={t("Service area")}
        description={
          areas.length > 0
            ? t("Jobs at an address inside a circle suggest this service first.")
            : t("No areas yet - the service is never suggested for a job's address.")
        }
        padded
        fullWidth
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {areas.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {areas.map((area, index) => (
                <Chip
                  key={`${area.city}|${area.county}|${area.region}`}
                  variant="outlined"
                  sx={{ borderColor: getAreaColor(index), borderWidth: 2, height: "auto", py: 0.5 }}
                  label={
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="body2" fontWeight={600} component="span">
                        {area.city}
                      </Typography>{" "}
                      <Typography variant="body2" component="span">
                        · {t("{{value}} km radius", { value: area.radius_km })}
                      </Typography>
                      {formatAreaPlace(area) && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {formatAreaPlace(area)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </Box>
          )}
          <ExternalServiceAreasMap areas={areas} height={360} />
        </Box>
      </DetailSection>

      <DetailMetadata createdAt={externalService.created_at} updatedAt={externalService.updated_at} />
    </DetailPage>
  );
};
